import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Home,
  FileText,
  Calendar,
  User,
  Clock,
  FolderOpen,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Sun,
  Moon,
  IndianRupee,
  BookOpen,
  HelpCircle,
  Building2,
  FileUser,
  UsersRound,
  Target,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { getEmployeeDetails } from "@/services/employeeService";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { useTeam } from "@/contexts/TeamContext";

import {
  getNotifications,
  MarkAsALLReadNotifications,
  MarkAsReadNotificationbyId,
  deleteAllNotification,
} from "@/services/employeeService";
import NotificationsMenu from "@/components/common/NotificationsMenu";
import {
  getEmployeePortalNotificationRoute,
  announceNotificationsChanged,
  onNotificationsChanged,
} from "@/lib/notifications";

/* -------------------------------------------------- */
/* User dropdown (shared by both headers)             */
/* -------------------------------------------------- */
const UserMenu = ({ user, userProfilePicture, onProfile, onLogout, triggerClassName }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" className={triggerClassName}>
        <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
          <AvatarImage src={userProfilePicture} />
          <AvatarFallback className="text-xs bg-primary text-white">
            {user?.username?.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="hidden md:inline text-sm font-medium">
          {user?.username?.split(" ")[0]}
        </span>
        <ChevronDown className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>{user?.username}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onProfile}>
        <User className="mr-2 h-4 w-4" /> Profile
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-red-600" onClick={onLogout}>
        <LogOut className="mr-2 h-4 w-4" /> Log out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/* Colour treatment mirrors the LEARN sidebar: one dark surface in both themes,
   muted foreground, white on hover, and a translucent teal accent wash + hairline
   ring on the active item. The tokens themselves shift between light/dark, so
   these classes deliberately carry no `dark:` overrides. */
const SIDEBAR_ICON_BTN =
  "text-sidebar-muted hover:bg-white/10 hover:text-white";

const SIDEBAR_ITEM_IDLE =
  "text-sidebar-foreground/90 hover:bg-white/[0.06] hover:text-white";

const SIDEBAR_ITEM_ACTIVE =
  "bg-sidebar-accent/15 text-white font-semibold hover:bg-sidebar-accent/20 hover:text-white " +
  "shadow-[inset_0_0_0_1px_hsl(var(--sidebar-accent)/0.25)] " +
  "before:content-[''] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-1 " +
  "before:-translate-y-1/2 before:rounded-r-full before:bg-sidebar-accent " +
  "dark:font-medium dark:before:hidden";

const SIDEBAR_LOGOUT =
  "text-sidebar-foreground/90 hover:bg-white/10 hover:text-white";

/* Mobile bottom sheet — same surface colours as the sidebar, minus the active
   item's accent rail (the sheet has no rail to begin with). */
const DRAWER_ITEM_ACTIVE =
  "bg-sidebar-accent/15 text-white hover:bg-sidebar-accent/20 " +
  "shadow-[inset_0_0_0_1px_hsl(var(--sidebar-accent)/0.25)]";

const DRAWER_LOGOUT =
  "text-sidebar-foreground/90 hover:bg-white/[0.06] hover:text-white";

const EmployeeLayout = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { hasFeature, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [employeeData, setEmployeeData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [userProfilePicture, setUserProfilePicture] = useState();
  const { addNotificationListener } = useWebSocket();
  const [logoFailed, setLogoFailed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Tablet (md–lg) overlay drawer open state.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Expanded nav groups, keyed by group label.
  const [openGroups, setOpenGroups] = useState({});
  // Employees who manage people get their team's pages (manager duties).
  const team = useTeam();

  useEffect(() => {
    fetchEmployeeData();
    fetchNotifications();

    const unsubscribe = addNotificationListener((newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      toast.info(newNotification.title, {
        description: newNotification.message,
      });
    });
    return () => unsubscribe();
  }, []);

  const showBranding = hasFeature("custom_branding");

  // The Notifications page (or another bell) changed read / cleared state.
  useEffect(() => onNotificationsChanged(() => fetchNotifications()), []);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();
      const sortedNotifications = response.data.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setNotifications(sortedNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      const response = await getEmployeeDetails();
      setUserProfilePicture(response.profilePhotoSignedUrl);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  // Flat nav (no section headings). A `{ type: "divider" }` marker splits the
  // primary items from the "Account" group (Documents + Profile) with a thin
  // separator line — no text heading. An item with `items` is an expandable
  // group; its children carry their own feature gates.
  const allNavigationItems = [
    { path: "/employee/dashboard", label: "Dashboard", icon: Home },
    { path: "/employee/attendance", label: "Attendance", icon: Clock },
    {
      path: "/employee/leave",
      label: "Leave",
      icon: Calendar,
      featureRequired: ["basic_leave_management", "custom_leave_management"],
    },
    { path: "/employee/payslips", label: "Payslips", icon: FileText },
    { path: "/employee/finances", label: "Finances", icon: IndianRupee },
    {
      path: "/employee/reviews",
      label: "Reviews",
      icon: BookOpen,
      featureRequired: ["performance_management"],
    },
    ...(team.isManager
      ? [
          {
            label: "My team",
            icon: UsersRound,
            // Also active on pages without their own entry (a review, a PIP).
            activePrefix: "/employee/team/",
            items: [
              team.duties.leave && {
                path: "/employee/team/leave",
                label: "Leave approvals",
                icon: Calendar,
                featureRequired: ["basic_leave_management", "custom_leave_management"],
              },
              (team.duties.appraisals || team.duties.pips) && {
                path: "/employee/team/reviews",
                label: "Team reviews",
                icon: BookOpen,
                featureRequired: ["performance_management"],
              },
              team.duties.goals && {
                path: "/employee/team/goals",
                label: "Team goals",
                icon: Target,
                featureRequired: ["performance_management"],
              },
            ].filter(Boolean),
          },
        ]
      : []),
    { type: "divider" },
    {
      label: "Documents",
      icon: FolderOpen,
      items: [
        {
          path: "/employee/documents",
          label: "My Documents",
          icon: FileUser,
          featureRequired: ["document_upload"],
        },
        {
          path: "/employee/company-documents",
          label: "Company Documents",
          icon: Building2,
        },
      ],
    },
    { path: "/employee/profile", label: "Profile", icon: User },
    // { path: "/employee/user-manual", label: "Help", icon: BookOpen },
  ];

  const isAvailableOnPlan = (item) =>
    subscriptionLoading || !item.featureRequired || hasFeature(item.featureRequired);

  // 1) Keep dividers, drop feature-gated items the plan doesn't include. A group
  //    left with one child becomes that child — no single-entry dropdown — and
  //    an empty group disappears.
  const visibleNavigationItems = allNavigationItems
    .map((item) => {
      if (!item.items) return item;
      const children = item.items.filter(isAvailableOnPlan);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { ...item, items: children };
    })
    .filter((item) => item && (item.type === "divider" || item.items || isAvailableOnPlan(item)));

  // 2) Guard the divider: only render it when real items sit on BOTH sides, so
  //    it never leads, trails, or dangles when adjacent items are hidden.
  const navigationItems = visibleNavigationItems.filter((item, i, arr) => {
    if (item.type !== "divider") return true;
    const hasBefore = arr.slice(0, i).some((it) => it.type !== "divider");
    const hasAfter = arr.slice(i + 1).some((it) => it.type !== "divider");
    return hasBefore && hasAfter;
  });

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setSidebarOpen(false);
  };

  const isGroupActive = (group) =>
    (group.activePrefix && location.pathname.startsWith(group.activePrefix)) ||
    group.items.some((child) => location.pathname === child.path);

  const toggleGroup = (label) =>
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  // Arriving on a grouped page (nav click, notification, dashboard link) opens
  // its group so the active item is visible.
  const activeGroupLabel = navigationItems.find((item) => item.items && isGroupActive(item))?.label;
  useEffect(() => {
    if (activeGroupLabel) {
      setOpenGroups((prev) => (prev[activeGroupLabel] ? prev : { ...prev, [activeGroupLabel]: true }));
    }
  }, [activeGroupLabel, location.pathname]);

  const navTestId = (label) =>
    `employee-sidebar-${label.toLowerCase().replace(/\s+/g, "-")}-link`;

  const handleLogout = () => {
    logout();
  };

  const markAllAsRead = async () => {
    try {
      await MarkAsALLReadNotifications();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
      announceNotificationsChanged();
      if (window.refreshSidebarNotifications) {
        window.refreshSidebarNotifications();
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const clearAllNotifications = async () => {
    try {
      await deleteAllNotification();
      setNotifications([]);
      toast.success("All notifications cleared");
      announceNotificationsChanged();
    } catch (error) {
      toast.error("Failed to clear notifications");
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      await MarkAsReadNotificationbyId(notification._id, true);

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notification._id ? { ...n, is_read: true } : n
        )
      );

      announceNotificationsChanged();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    // Where to go: shared with the Notifications page (lib/notifications.js).
    const route = getEmployeePortalNotificationRoute(user?.employee_id)(notification);
    if (route?.path) {
      navigate(route.path, route.state ? { state: route.state } : undefined);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    // Edge-to-edge shell: fills the whole viewport (ignoring the body's safe-area
    // padding) so the UI runs under the status bar / home indicator like a modern
    // app. Safe-area insets are handled by the header (top) and content (bottom).
    <div className="fixed inset-0 overflow-hidden bg-background">
      {/* Tablet (md–lg) drawer backdrop — overlays the body when the sidebar
          is open, instead of pushing content to the right. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:block lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ============================================================
          SIDEBAR — fixed, full height.
          • Desktop (lg+): always visible, content is pushed (lg:ml-*).
          • Tablet (md–lg): off-canvas drawer that overlays the body.
          • Mobile (< md): hidden (uses the top bar + hamburger instead).
         ============================================================ */}
      <aside
        className={`hidden md:flex fixed left-0 top-0 z-50 h-full flex-col bg-sidebar-surface text-sidebar-foreground border-r border-sidebar-border shadow-xl shadow-black/5 dark:shadow-none transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? "w-[76px]" : "w-[264px]"
        } ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        {/* Branding + collapse toggle */}
        <div className="flex items-center justify-between p-4 border-b border-sidebar-border">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 rounded-lg bg-transparent p-1 shadow-none dark:p-0">
                <img
                  src="/zenith-logo-no-name.png"
                  alt="Zenith Logo"
                  className="w-auto h-8"
                />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold text-lg text-white leading-tight">
                  Zenith
                </h1>
                <p className="text-xs text-sidebar-muted truncate">
                  {showBranding && user?.company_name
                    ? user.company_name
                    : "Employee Portal"}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-1 shrink-0">
            {/* Collapse / expand (desktop only) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed((v) => !v)}
              className={`hidden lg:flex h-8 w-8 ${SIDEBAR_ICON_BTN}`}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="employee-sidebar-toggle"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>

            {/* Close drawer (tablet only) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className={`flex lg:hidden h-8 w-8 ${SIDEBAR_ICON_BTN}`}
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Navigation (scrollable) */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navigationItems.map((item, i) => {
            if (item.type === "divider") {
              return (
                <div
                  key={`divider-${i}`}
                  role="separator"
                  className={`h-px bg-sidebar-border ${
                    sidebarCollapsed ? "mx-1 my-2" : "mx-2 my-2"
                  }`}
                />
              );
            }
            const Icon = item.icon;

            if (item.items) {
              const groupActive = isGroupActive(item);

              // Collapsed rail: the group opens as a flyout, like the admin sidebar.
              if (sidebarCollapsed) {
                return (
                  <Popover key={item.label}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        title={item.label}
                        aria-label={item.label}
                        className={`relative w-full h-10 justify-center px-0 ${
                          groupActive ? SIDEBAR_ITEM_ACTIVE : SIDEBAR_ITEM_IDLE
                        }`}
                        data-testid={navTestId(item.label)}
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${groupActive ? "text-sidebar-accent" : ""}`} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="start" className="w-56 p-2">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold mb-1 px-2 text-muted-foreground">
                          {item.label}
                        </p>
                        {item.items.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = location.pathname === child.path;
                          return (
                            <Button
                              key={child.path}
                              variant={childActive ? "secondary" : "ghost"}
                              className="w-full justify-start h-9 text-sm"
                              onClick={() => handleNavigation(child.path)}
                              data-testid={navTestId(child.label)}
                            >
                              <ChildIcon className="h-3.5 w-3.5 mr-2" />
                              {child.label}
                            </Button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }

              const expanded = Boolean(openGroups[item.label]);
              const groupId = `employee-nav-group-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
              return (
                <div key={item.label}>
                  <Button
                    variant="ghost"
                    onClick={() => toggleGroup(item.label)}
                    aria-expanded={expanded}
                    aria-controls={groupId}
                    className={`relative w-full h-10 px-3 justify-start ${SIDEBAR_ITEM_IDLE} ${
                      groupActive ? "text-white" : ""
                    }`}
                    data-testid={navTestId(item.label)}
                  >
                    <Icon
                      className={`h-4 w-4 shrink-0 mr-3 ${groupActive ? "text-sidebar-accent" : ""}`}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronRight
                      className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                        expanded ? "rotate-90" : ""
                      }`}
                    />
                  </Button>
                  {expanded && (
                    <div
                      id={groupId}
                      className="ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-2 animate-slide-in"
                    >
                      {item.items.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = location.pathname === child.path;
                        return (
                          <Button
                            key={child.path}
                            variant="ghost"
                            onClick={() => handleNavigation(child.path)}
                            className={`relative w-full h-9 px-3 justify-start text-sm ${
                              childActive ? SIDEBAR_ITEM_ACTIVE : SIDEBAR_ITEM_IDLE
                            }`}
                            data-testid={navTestId(child.label)}
                          >
                            <ChildIcon
                              className={`h-3.5 w-3.5 shrink-0 mr-2.5 ${
                                childActive ? "text-sidebar-accent" : ""
                              }`}
                            />
                            <span className="flex-1 text-left truncate">{child.label}</span>
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                variant="ghost"
                onClick={() => handleNavigation(item.path)}
                title={sidebarCollapsed ? item.label : undefined}
                className={`relative w-full h-10 px-3 ${
                  sidebarCollapsed ? "justify-center px-0" : "justify-start"
                } ${isActive ? SIDEBAR_ITEM_ACTIVE : SIDEBAR_ITEM_IDLE}`}
                data-testid={navTestId(item.label)}
              >
                <Icon
                  className={`h-4 w-4 shrink-0 ${!sidebarCollapsed && "mr-3"} ${
                    isActive ? "text-sidebar-accent" : ""
                  }`}
                />
                {!sidebarCollapsed && (
                  <span className="flex-1 text-left">{item.label}</span>
                )}
              </Button>
            );
          })}
        </nav>

        {/* Logout footer */}
        <div className="mt-auto p-3 border-t border-sidebar-border">
          <Button
            variant="ghost"
            onClick={handleLogout}
            title={sidebarCollapsed ? "Logout" : undefined}
            className={`w-full h-10 px-3 ${SIDEBAR_LOGOUT} ${
              sidebarCollapsed ? "justify-center px-0" : "justify-start"
            }`}
            data-testid="employee-sidebar-logout-btn"
          >
            <LogOut className={`h-4 w-4 shrink-0 ${!sidebarCollapsed && "mr-3"}`} />
            {!sidebarCollapsed && "Logout"}
          </Button>
        </div>
      </aside>

      {/* ============================================================
          CONTENT COLUMN — shifted right of the sidebar on desktop.
         ============================================================ */}
      <div
        className={`flex flex-col h-full transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-[76px]" : "lg:ml-[264px]"
        }`}
      >
        {/* ---- DESKTOP HEADER (md and up) ---- */}
        <header className="hidden md:flex h-16 shrink-0 bg-card border-b border-border items-center justify-between px-6 z-30">
          {/* Left: greeting + date */}
          <div className="flex items-center gap-4 min-w-0">
            {/* Open drawer (tablet only) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="flex lg:hidden h-9 w-9 shrink-0"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {showBranding && user?.company_logo && !logoFailed && (
              <img
                src={user.company_logo}
                alt="Company Logo"
                onError={() => setLogoFailed(true)}
                className="h-8 w-8 object-contain rounded shrink-0"
              />
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground truncate">
                Welcome, {user?.username?.split(" ")[0] || user?.username}
              </h2>
              <p className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString("en-IN", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-3">
            <NotificationsMenu
              notifications={notifications}
              unreadCount={unreadCount}
              onNotificationClick={handleNotificationClick}
              onMarkAll={markAllAsRead}
              onClearAll={clearAllNotifications}
              onViewAll={() => navigate("/employee/notifications")}
              triggerClassName="relative h-9 w-9"
            />

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/help")}
              className="h-9 w-9"
              title="Help Center"
              data-testid="help-center-btn-desktop"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9"
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>

            <UserMenu
              user={user}
              userProfilePicture={userProfilePicture}
              onProfile={() => navigate("/employee/profile")}
              onLogout={handleLogout}
              triggerClassName="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3"
            />
          </div>
        </header>

        {/* ---- MOBILE HEADER (< md) — edge-to-edge: bg fills the status-bar /
             notch area (via safe-area top padding), content sits below it ---- */}
        <header
          className="md:hidden shrink-0 bg-card border-b border-border z-50 shadow-sm"
          style={{ paddingTop: "var(--safe-area-inset-top)" }}
        >
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col">
              {/* Main Header Row */}
              <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4">
                {/* Left: Logo & Branding */}
                <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0 flex-1 sm:flex-initial">
                  <div
                    className={`flex items-center gap-1.5 sm:gap-2 ${showBranding && "border-r border-border pr-2 sm:pr-3"}`}
                  >
                    <img
                      src="/zenith-logo-no-name.png"
                      alt="Zenith"
                      className="w-6 h-6 sm:w-7 sm:h-7"
                    />
                    <span className="hidden sm:inline text-foreground text-xs sm:text-sm font-semibold tracking-tight whitespace-nowrap">
                      Zenith HRMS
                    </span>
                  </div>

                  {showBranding && (
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 max-w-[140px] sm:max-w-[180px]">
                      {user?.company_logo && !logoFailed && (
                        <img
                          src={user.company_logo}
                          alt="Company Logo"
                          onError={() => setLogoFailed(true)}
                          className="h-6 w-6 sm:h-8 sm:w-8 object-contain flex-shrink-0 rounded"
                        />
                      )}
                      <div className="hidden sm:block min-w-0">
                        <h1 className="text-xs sm:text-sm font-bold leading-tight text-foreground truncate">
                          {user?.company_name}
                        </h1>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  <NotificationsMenu
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onNotificationClick={handleNotificationClick}
                    onMarkAll={markAllAsRead}
                    onClearAll={clearAllNotifications}
                    onViewAll={() => navigate("/employee/notifications")}
                    triggerClassName="relative sm:flex h-9 w-9 sm:h-10 sm:w-10"
                  />

                  {/* Help Center */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/help")}
                    className="hidden sm:flex h-9 w-9 sm:h-10 sm:w-10"
                    title="Help Center"
                    data-testid="help-center-btn-mobile"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>

                  {/* Theme Toggle */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleTheme}
                    className="hidden sm:flex h-9 w-9 sm:h-10 sm:w-10"
                  >
                    {isDarkMode ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </Button>

                  {/* User Menu */}
                  <UserMenu
                    user={user}
                    userProfilePicture={userProfilePicture}
                    onProfile={() => navigate("/employee/profile")}
                    onLogout={handleLogout}
                    triggerClassName="hidden sm:flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3"
                  />

                  {/* Mobile Menu Toggle */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="flex md:hidden h-9 w-9 sm:h-10 sm:w-10"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    {mobileMenuOpen ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Mobile Menu Overlay */}
              {mobileMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm md:hidden z-40 animate-in fade-in duration-300"
                    onClick={() => setMobileMenuOpen(false)}
                  />

                  {/* Menu Panel - 70% height from bottom with transition */}
                  <div
                    className="fixed inset-x-0 bottom-0 h-[70vh] bg-sidebar md:hidden z-40 overflow-y-auto border-t border-sidebar-border rounded-t-lg animate-in slide-in-from-bottom duration-300"
                    style={{ paddingBottom: "var(--safe-area-inset-bottom)" }}
                  >
                    <nav className="py-3 px-3 space-y-1">
                      {navigationItems.map((item, i) => {
                        if (item.type === "divider") {
                          return (
                            <div
                              key={`divider-${i}`}
                              role="separator"
                              className="mx-2 my-2 h-px bg-sidebar-border"
                            />
                          );
                        }
                        const Icon = item.icon;

                        if (item.items) {
                          const groupActive = isGroupActive(item);
                          const expanded = Boolean(openGroups[item.label]);
                          const groupId = `employee-sheet-group-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
                          return (
                            <div key={item.label}>
                              <Button
                                variant="ghost"
                                onClick={() => toggleGroup(item.label)}
                                aria-expanded={expanded}
                                aria-controls={groupId}
                                className={`w-full min-h-12 h-12 justify-start gap-3 px-3 text-[15px] ${SIDEBAR_ITEM_IDLE} ${
                                  groupActive ? "text-white" : ""
                                }`}
                              >
                                <Icon
                                  className={`h-5 w-5 shrink-0 ${groupActive ? "text-sidebar-accent" : ""}`}
                                />
                                <span className="flex-1 text-left">{item.label}</span>
                                <ChevronRight
                                  className={`h-5 w-5 shrink-0 transition-transform duration-200 ${
                                    expanded ? "rotate-90" : ""
                                  }`}
                                />
                              </Button>
                              {expanded && (
                                <div
                                  id={groupId}
                                  className="ml-5 mt-1 space-y-1 border-l border-sidebar-border pl-2"
                                >
                                  {item.items.map((child) => {
                                    const ChildIcon = child.icon;
                                    const childActive = location.pathname === child.path;
                                    return (
                                      <Button
                                        key={child.path}
                                        variant={childActive ? "default" : "ghost"}
                                        className={`w-full min-h-11 h-11 justify-start gap-3 px-3 text-[15px] ${
                                          childActive ? DRAWER_ITEM_ACTIVE : SIDEBAR_ITEM_IDLE
                                        }`}
                                        onClick={() => handleNavigation(child.path)}
                                      >
                                        <ChildIcon
                                          className={`h-4 w-4 shrink-0 ${
                                            childActive ? "text-sidebar-accent" : ""
                                          }`}
                                        />
                                        <span className="flex-1 text-left">{child.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }

                        const isActive = location.pathname === item.path;
                        return (
                          <Button
                            key={item.path}
                            variant={isActive ? "default" : "ghost"}
                            className={`w-full min-h-12 h-12 justify-start gap-3 px-3 text-[15px] ${
                              isActive ? DRAWER_ITEM_ACTIVE : SIDEBAR_ITEM_IDLE
                            }`}
                            onClick={() => handleNavigation(item.path)}
                          >
                            <Icon
                              className={`h-5 w-5 shrink-0 ${
                                isActive ? "text-sidebar-accent" : ""
                              }`}
                            />
                            <span className="flex-1 text-left">{item.label}</span>
                          </Button>
                        );
                      })}

                      {/* Mobile-only items */}
                      <div className="pt-3 mt-3 border-t border-sidebar-border space-y-1">
                        <Button
                          variant="ghost"
                          className={`w-full min-h-12 h-12 justify-start gap-3 px-3 text-[15px] ${SIDEBAR_ITEM_IDLE}`}
                          onClick={() => handleNavigation("/help")}
                          data-testid="help-center-btn-sheet"
                        >
                          <HelpCircle className="h-5 w-5 shrink-0" />
                          <span className="flex-1 text-left">Help & Support</span>
                        </Button>

                        <Button
                          variant="ghost"
                          onClick={toggleTheme}
                          className={`sm:hidden w-full min-h-12 h-12 justify-start gap-3 px-3 text-[15px] ${SIDEBAR_ITEM_IDLE}`}
                        >
                          {isDarkMode ? (
                            <Sun className="h-5 w-5 shrink-0" />
                          ) : (
                            <Moon className="h-5 w-5 shrink-0" />
                          )}
                          <span className="flex-1 text-left">Toggle Theme</span>
                        </Button>

                        <Button
                          variant="ghost"
                          className={`w-full min-h-12 h-12 justify-start gap-3 px-3 text-[15px] ${DRAWER_LOGOUT}`}
                          onClick={handleLogout}
                        >
                          <LogOut className="h-5 w-5 shrink-0" />
                          <span className="flex-1 text-left">Log out</span>
                        </Button>
                      </div>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* ---- MAIN CONTENT (the only scroll region) ---- */}
        <main className="flex-1 min-h-0 overflow-y-auto">
          <div
            className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-6"
            style={{
              paddingBottom: "calc(1.5rem + var(--safe-area-inset-bottom))",
            }}
          >
            <Outlet
              context={{ employeeData, refreshEmployeeData: fetchEmployeeData }}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default EmployeeLayout;
